import * as React from 'react';
import * as styles from './TreeTargetingForGroups.css';
import { INode } from '../../interfaces/INode';
import { LocalizationService } from '../../services/LocalizationService';
import { ITargetingGroup } from '../../interfaces/ITargetingGroup';
import { ITargetingApplications } from '../../interfaces/ITargetingApplications';
import { ITreeViewItem } from '../../interfaces/ITreeViewItem';
import { IATreeview } from '../treeview/Treeview';
import { IATooltip } from '../tooltip/Tooltip';
import { IAIcon } from '../icon/Icon';
import { IAToggle } from '../toggle/Toggle';
import { TargetingV2Service } from '../../services/TargetingV2Service';
import { Helper } from '../../Helper';
import { ITargetingV2Group } from '../targetingV2/interfaces/ITargetingV2Group';
import { IASkeleton } from '../skeleton/Skeleton';

export interface ITreeTargetingForGroupsProps {
  environment: "Development" | "Test" | "Production";
  tenant: string
  application: "messages" | "navigator" | "toolbox" | "events" | "content organizer" | "app" | "rss";
  applicationData?: ITargetingApplications;
  selectedGroupIds: number[];
  showTarget: boolean;
  highlightColor: string;
  instance?: string;
  label?: string;
  onShowTargetChanged: (boolean) => void;
  hideOptions?: boolean;
  showSelectAll?: boolean;
  required?: boolean;
  toolTip?: string;
  disabled?: boolean;
  onGroupIdsUpdated?: (ids: number[]) => void;
}

export interface ITreeTargetingForGroupsState {
  nodes: INode[];
  showTarget: boolean;
  selectAll?: boolean;
  groups: ITreeViewItem[];
  applicationData: ITargetingApplications;
  isLoadingGroups: boolean;
  availableGroups?: ITargetingV2Group[];
}

export class TreeTargetingForGroups extends React.Component<ITreeTargetingForGroupsProps, ITreeTargetingForGroupsState> {

  private inputCheckboxRef: React.RefObject<HTMLInputElement>;

  public constructor(props: ITreeTargetingForGroupsProps) {
    super(props);
    this.state = {
      nodes: [],
      showTarget: props.showTarget,
      groups: [],
      applicationData: props.applicationData,
      isLoadingGroups: props.applicationData == undefined,
    };
    this.inputCheckboxRef = React.createRef();
  }

  public componentDidMount(): void {
    this.getStructuredGroups();
    if (this.props.applicationData == undefined) {
      this.getTargetingGroups();
    }
  }

  public componentWillReceiveProps(props: ITreeTargetingForGroupsProps): void {
    if ((props.application !== this.props.application) && !props.applicationData) {
      this.setState({
        showTarget: props.showTarget,
      }, () => this.renderNodes(props.selectedGroupIds));
    }
    if (props.showTarget !== this.props.showTarget) {
      this.setState({
        showTarget: props.showTarget,
      }, () => this.renderNodes(props.selectedGroupIds));
    }
    if (props.selectedGroupIds !== this.props.selectedGroupIds) {
      this.renderNodes(props.selectedGroupIds);
    }
    if (props.instance !== this.props.instance) {
      this.renderNodes(props.selectedGroupIds);
    }
  }

  private getTargetingGroups(): void {
    this.setState({ isLoadingGroups: true }, () => {
      TargetingV2Service.getTargetingGroups(this.props.environment, this.props.tenant, this.props.application, this.props.instance).then((response) => {
        const availableGroups: ITargetingV2Group[] = response.groupEntries;
        this.setState({ availableGroups, isLoadingGroups: false }, () => this.renderNodes(this.props.selectedGroupIds));
      }).catch(() => {
        console.log("failed getting targeting groups");
      }).finally(() => {
        this.setState({ isLoadingGroups: false });
      });
    });
  }

  private getStructuredGroups(): void {
    const groups = localStorage.getItem("IA_TargetingStructure");
    if (groups) {
      this.setState({ groups: JSON.parse(groups) });
    }
    this.setState({ isLoadingGroups: true }, () => {
      TargetingV2Service.getTargetingStructure(this.props.environment, this.props.tenant).then((groups: ITreeViewItem[]) => {
        this.setState({ groups, isLoadingGroups: false }, () => this.renderNodes(this.props.selectedGroupIds));
        if (groups) {
          Helper.setLocalStorage("IA_TargetingStructure", JSON.stringify(groups));
        }
      });
    });
  }

  private renderNodes = (selectedValues) => {
    const application = this.state.applicationData;
    let nodes: INode[] = [];
    // If applicationData is parsed
    if (application) {
      let groups = application ? application.targetingGroups : undefined;
      // Checking if the current instance will use default values or not
      if (application.application) {
        const currentInstanceSettings = application.application.instances.find(x => x.name === this.props.instance || x.id === this.props.instance || ((this.props.instance === "Default" && x.name === this.props.instance) || (this.props.instance === "Default" && x.id === "")));
        if (currentInstanceSettings) {
          // Checking if will not use Default AND that the Current instance type is USER GROUPS
          if (!currentInstanceSettings.useDefault) {
            groups = [];
            if (currentInstanceSettings.values && currentInstanceSettings.values.length > 0) {
              currentInstanceSettings.values.forEach((v) => {
                if (v.value) {
                  groups.push({
                    id: v.id,
                    title: v.label,
                    values: undefined
                  });
                }
              });
            }
          } else {
            const groupDataApplication = application.application.default && application.application.default.values ? application.application.default.values.filter(x => x.value) : undefined;
            groups = [];

            if (groupDataApplication) {
              groupDataApplication.forEach((g) => {
                groups.push({
                  id: g.id,
                  title: g.label,
                  values: undefined
                });
              });
            }
          }
        }
      }
      if (!!groups && groups.length > 0) {
        groups = groups.sort((g1, g2) => {
          if (g1.title.toLocaleLowerCase() > g2.title.toLocaleLowerCase()) {
            return 1;
          }
          if (g1.title.toLocaleLowerCase() < g2.title.toLocaleLowerCase()) {
            return -1;
          }
          return 0;
        });
        groups.forEach(group => {
          let node = this.convertGroupToNode(group);
          node = this.verifyCheckedGroupDataNode(node, selectedValues);
          nodes.push({
            defaultLabel: node.defaultLabel,
            checked: node.checked,
            childNodes: [],
            id: node.id,
            labels: [],
            columnName: group.title,
            title: group.title,
            markedChild: false,
          });
        });
      }
      // Diplaying the results for the administrator
      this.setState({ nodes, selectAll: nodes.length === selectedValues?.length });


      // If groups are fetched from TargetingV2Service
    } else if (this.state.availableGroups) { //
      const nodes = [];
      this.state.availableGroups.forEach(((group) => {
        nodes.push({
          checked: selectedValues.find((x) => `${x}` === `${group.id}`),
          childNodes: [],
          id: group.id,
          columnName: group.label,
          title: group.label,
          markedChild: false,
          highlighted: group.highlighted
        });
      }));
      this.setState({ nodes });
    }
  }

  private convertGroupToNode(group: ITargetingGroup) {
    const list: INode[] = [];
    const node: INode = {
      childNodes: list,
      id: group.id,
      defaultLabel: group.title,
      title: group.title,
      checked: false,
      labels: []
    };
    return node;
  }

  private verifyCheckedGroupDataNode = (node: INode, checkedList: number[]): INode => {
    if (node.id) {
      const items = checkedList ? checkedList.filter(l => l && l.toString() === node.id.toString()) : [];
      if (items && items.length > 0) {
        node.checked = true;
      }
    }
    return node;
  }

  private getSkeleton(): JSX.Element {
    return <>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "60%",
          borderRadius: 5,
        }}
        />
      </div>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "80%",
          borderRadius: 5,
        }}
        />
      </div>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "70%",
          borderRadius: 5,
        }}
        />
      </div>
      <div className={styles.IA_checkboxSkeletonWrapper}>
        <IASkeleton skeleton={{
          height: 20,
          width: 20,
          borderRadius: 2,
          marginRight: 10
        }}
        />
        <IASkeleton skeleton={{
          height: 20,
          width: "50%",
          borderRadius: 5,
        }}
        />
      </div>
    </>
  }

  public render(): React.ReactElement<ITreeTargetingForGroupsProps> {
    let label = <div style={{ float: "left" }}>{this.props.label}</div>;
    if (this.props.required) {
      label = <div style={{ float: "left" }}>{this.props.label} <span style={{ color: "red" }}>*</span></div>;
    }
    return (
      <div
        style={{
          clear: "both"
        }}
      >
        {!this.props.hideOptions ?
          <div
            style={{
              clear: "both"
            }}
          >
            <IAToggle
              label={this.props.label !== undefined ? this.props.label : new LocalizationService().strings.TargetTree_WhoShouldSeeThis}
              checked={this.state.showTarget}
              color={this.props.highlightColor}
              toolTip={this.props.toolTip}
              onChange={() => {
                if (this.state.showTarget) {
                  this.props.onGroupIdsUpdated([]);
                }
                this.props.onShowTargetChanged(!this.state.showTarget);
                this.setState({ showTarget: !this.state.showTarget });
              }}
            />
          </div>
          :
          <div>
            {label}
            {this.props.toolTip && this.props.toolTip !== "" &&
              <IATooltip
                content="Tooltip"
                type={window.innerWidth < 667 ? "popup" : "bubble"}
                styles={{
                  textAlign: "left",
                  color: "#333333"
                }}
                componentContent={
                  <div>
                    {this.props.toolTip}
                  </div>
                }
              >
                <IAIcon
                  title="Info"
                  size={14}
                  color={this.props.highlightColor !== undefined ? this.props.highlightColor : "#03787c"}
                  style={{
                    float: "left",
                    cursor: "pointor",
                    marginLeft: 4,
                    marginTop: 2,
                    height: 13
                  }}
                />
              </IATooltip>
            }
          </div>
        }
        {this.state.showTarget &&
          <>

            {this.state.isLoadingGroups
              ?
              this.getSkeleton()
              :
              <>
                <label className={styles.IAcheckbox}>
                  <input
                    type="checkbox"
                    ref={this.inputCheckboxRef}
                    tabIndex={-1}
                    checked={this.state.selectAll}
                    onChange={() => {
                      this.setState({ selectAll: this.state.selectAll ? !this.state.selectAll : true }, () => {
                        this.updateAllNodes(this.state.selectAll);
                        if (this.state.selectAll) {
                          const allIds = []
                          this.state.nodes.forEach((targetingGroup: INode) => {
                            allIds.push(targetingGroup.id);
                          })
                          this.props.onGroupIdsUpdated(allIds);
                        } else {
                          this.props.onGroupIdsUpdated([]);
                        }
                      });
                    }}
                  />
                  <div
                    tabIndex={0}
                    className={[styles.IAcheck, "IA_OnMouseFocusOutline"].join(" ")}
                    style={{
                      backgroundColor: this.state.selectAll ? this.props.highlightColor : "#ffffff",
                      borderColor: this.state.selectAll ? this.props.highlightColor : "#d9d9d9",
                      boxSizing: "border-box"
                    }}
                    onKeyPress={(event) => {
                      if (event.key === "Enter") {
                        this.inputCheckboxRef.current.click();
                      }
                    }}
                  />

                  <div className={styles.IAcaption} >
                    {new LocalizationService().strings.TargetTree_All}
                  </div>
                </label>
                <IATreeview
                  items={this.getChildren(this.state.groups)}
                  highlightColor={this.props.highlightColor}
                  actionType="Checkbox"
                  structureType="Arrow"
                  expanded={true}
                  onChange={(items: ITreeViewItem[]) => {
                    let selectedIds = [];
                    items.forEach((item: ITreeViewItem) => {
                      if (item.id && item.checked) {
                        selectedIds.push(item.id);
                      }
                      if (item.children) {
                        selectedIds = selectedIds.concat(this.getSelectedIds(item.children));
                      }
                    });
                    if (this.props.onGroupIdsUpdated) {
                      this.props.onGroupIdsUpdated(selectedIds);
                    }
                    this.forceUpdate();
                    // if all are selected set to true
                    this.setState({ selectAll: this.state.nodes.length === selectedIds.length });
                  }}
                />
              </>
            }
          </>
        }
      </div>
    );
  }

  private checkIfAllChildrenAreSelected = (parent: INode, itemsCount, checkedCount): [number, number] => {
    if (parent.childNodes && parent.childNodes.length > 0) {
      parent.childNodes.forEach((child) => {
        const result = this.checkIfAllChildrenAreSelected(child, itemsCount, checkedCount);
        itemsCount = result[0];
        checkedCount = result[1];
      });
    }
    if (parent.checked) {
      checkedCount++;
    }
    itemsCount++;
    return [itemsCount, checkedCount];
  }

  private updateAllNodes(value: boolean) {
    const nodes = this.state.nodes;
    nodes.forEach((node) => {
      node.checked = value;
      node.markedChild = value;
      if (node.childNodes != null && node.childNodes.length > 0) {
        this.updateNodes(node.childNodes, value);
      }
    });
  }

  private updateNodes(nodes: INode[], value: boolean) {
    nodes.forEach((node) => {
      node.checked = value;
      node.markedChild = value;
      if (node.childNodes != null && node.childNodes.length > 0) {
        this.updateNodes(node.childNodes, value);
      }
    });
  }

  private hasEnabledChildren(children: ITreeViewItem[], enabledChild): boolean {
    let hasEnabledChild = enabledChild;
    this.state.nodes.forEach((dataItem) => {
      children?.forEach((child: ITreeViewItem) => {
        if (`${child.id}` === `${dataItem.id}`) {
          hasEnabledChild = true;
        }
        if (child.children) {
          hasEnabledChild = this.hasEnabledChildren(child.children, hasEnabledChild);
        }
      })
    });
    return hasEnabledChild;
  }

  private areAllChildrenAreChecked(children: ITreeViewItem[], allChildrenAreChecked): boolean {
    let allChecked = allChildrenAreChecked;
    const enabledChildren = [];
    children.forEach((child: ITreeViewItem) => {
      if (child.id == undefined) {
        enabledChildren.push(child);
      }
      this.state.nodes.forEach((dataItem) => {
        if (`${child.id}` === `${dataItem.id}`) {
          enabledChildren.push(child);
        }
      });
    });
    enabledChildren?.forEach((child: ITreeViewItem) => {
      let childExits = false;
      this.props.selectedGroupIds?.forEach((selectedValue) => {
        if (`${selectedValue}` === `${child.id}`) {
          childExits = true;
        }
      });
      if (child.id == undefined) {
        childExits = true;
      }
      if (!childExits) {
        allChecked = false;
      }
      if (child.children) {
        allChecked = this.areAllChildrenAreChecked(child.children, allChecked);
      }
    });
    return allChecked;
  }

  private getChildren(items: ITreeViewItem[]): ITreeViewItem[] {
    let groupChildren: ITreeViewItem[] = [];
    if (items.length > 0) {
      items.forEach((childGroup: ITreeViewItem) => {
        let groupChild: ITreeViewItem;
        this.state.nodes.forEach((dataItem) => {
          if (`${childGroup.id}` === `${dataItem.id}`) {
            groupChild = {
              ...dataItem,
              highlighted: childGroup.highlighted,
              children: childGroup.children,
            };
          }
        });
        if (!groupChild) {
          groupChild = childGroup;
        }
        if (groupChild.title && (groupChild.id || (!groupChild.id && this.hasEnabledChildren(childGroup.children, false)))) { // If groupChild is a AD group or a group header that has enabled children
          groupChildren.push(
            {
              title: groupChild.title,
              id: groupChild.id,
              highlighted: groupChild.highlighted,
              children: groupChild.children?.length > 0 ? this.getChildren(groupChild.children) : [],
              checked: this.state.selectAll || groupChild.checked || (groupChild?.children?.length > 0 && this.areAllChildrenAreChecked(groupChild?.children, true))
            }
          );
        } else {
          if (this.hasEnabledChildren(childGroup.children, false)) {
            this.getChildren(groupChild.children).forEach((groupChildChild) => {
              groupChildren.push(groupChildChild);
            });
          }
        }
      });
    } else {
      groupChildren = this.state.nodes;
    }
    return groupChildren;
  }

  private getSelectedIds(items: ITreeViewItem[]): number[] {
    let selectedIds = [];
    items.forEach((item: ITreeViewItem) => {
      if (item.id && item.checked) {
        selectedIds.push(item.id);
      }
      if (item.children) {
        selectedIds = selectedIds.concat(this.getSelectedIds(item.children));
      }
    });
    return selectedIds;
  }
}